'use client'

import Image from 'next/image'
import Link from 'next/link'

interface TemplateCardProps {
  id: string
  title: string
  description: string
  logo?: string
  message?: string
  agent?: string
  /** Smaller text and padding, e.g. for dashboard templates section */
  compact?: boolean
}

export default function TemplateCard({
  title,
  description,
  logo,
  message,
  agent,
  compact = false,
}: TemplateCardProps) {
  const href = message
    ? `/copilot?message=${encodeURIComponent(message)}${agent ? `&agent=${encodeURIComponent(agent)}` : ''}`
    : '/copilot'

  const cardClassName = compact
    ? 'group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl bg-white p-3 transition-transform duration-300 hover:scale-[1.02] hover:shadow-lg sm:p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900'
    : 'group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl bg-white p-4 transition-transform duration-300 hover:scale-[1.02] hover:shadow-lg sm:p-5 md:p-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900'

  return (
    <Link href={href} className={cardClassName}>
      <div className="relative z-10 flex h-full flex-col">
        {logo && (
          <div className={compact ? 'mb-2' : 'mb-3 sm:mb-4'}>
            <Image
              src={logo}
              alt=""
              width={32}
              height={32}
              className={compact ? 'h-6 w-6 object-contain' : 'h-8 w-8 object-contain'}
            />
          </div>
        )}

        <h3
          className={
            compact
              ? 'mb-1.5 w-full whitespace-pre-wrap break-words text-left text-base font-semibold leading-tight text-black'
              : 'mb-2 w-full whitespace-pre-wrap break-words text-left text-lg font-normal leading-none text-black sm:mb-3 sm:text-xl md:text-[22px]'
          }
          style={
            compact
              ? undefined
              : {
                  fontFamily: '"Inter Variable", "Inter Variable Placeholder", sans-serif',
                  letterSpacing: '-0.02em',
                  fontFeatureSettings: "'cv01' on, 'cv09' on, 'cv11' on, 'cv05' on, 'ss03' on",
                  fontVariationSettings: '"opsz" 32, "wght" 600',
                  position: 'relative',
                }
          }
        >
          {title}
        </h3>

        <p
          className={
            compact
              ? 'w-full max-w-full flex-grow whitespace-pre-wrap break-words text-left text-sm leading-[1.2] text-gray-600'
              : 'w-full max-w-full flex-grow whitespace-pre-wrap break-words text-left text-base font-normal leading-[1.2] text-[#999999] sm:w-[250px] sm:text-lg'
          }
          style={
            compact
              ? undefined
              : {
                  fontFamily: '"Inter Variable", "Inter Variable Placeholder", sans-serif',
                  letterSpacing: '-0.01em',
                  fontFeatureSettings: "'cv09' on, 'cv01' on, 'cv05' on, 'ss03' on",
                  fontVariationSettings: '"opsz" 32, "wght" 500',
                  position: 'relative',
                }
          }
        >
          {description}
        </p>
      </div>
    </Link>
  )
}
